import type { AffinityResult, TopMatchResult } from '../types';

export interface GetTopMatchesOptions {
    limit?: number;
    minScore?: number;
    excludeIds?: string[];
    /** Number of users sampled per tag query. Default 200. Raise at scale (future). */
    scanDepth?: number;
}

/**
 * Client wrapper around the computeTagAffinity and getTopAffinityMatches
 * callable Cloud Functions.
 *
 * All heavy lifting — tag union, intersection, cache reads/writes — happens
 * server-side. This service is a thin HTTP-to-types bridge.
 *
 * Future optimisation: at 50k+ users, replace getTopMatches with a
 * fan-out tag index (tagIndex/{tag}/members/{userId}) so queries are
 * O(tags × indexPageSize) instead of O(total users).
 */
export const TagAffinityService = {

    /**
     * Returns the tag-overlap affinity between two users.
     * Either userIdA or userIdB must equal the calling user's UID.
     *
     * Returns cached data if neither user has updated their tags since the last
     * computation (determined by comparing computedAt against tagsUpdatedAt on
     * each user document). accessCount is incremented on every cache hit.
     */
    async computeAffinity(userIdA: string, userIdB: string): Promise<AffinityResult> {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fn = httpsCallable(getFunctions(getApp()), 'computeTagAffinity');
        const result = await fn({ userIdA, userIdB });
        return result.data as AffinityResult;
    },

    /**
     * Returns users sorted by tag-overlap score with the given userId.
     * Powers the Similar Interests deck in Discover.
     *
     * Default: limit=10, minScore=4, excludeIds=[], scanDepth=200.
     *
     * Pass the calling user's friend IDs + blocked IDs as excludeIds so they
     * never appear in results. At scale (50k+ users), server-side friend-list
     * lookup would be cleaner — noted as a future optimisation.
     */
    async getTopMatches(
        userId: string,
        options: GetTopMatchesOptions = {}
    ): Promise<TopMatchResult[]> {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fn = httpsCallable(getFunctions(getApp()), 'getTopAffinityMatches');
        const result = await fn({
            userId,
            limit: options.limit ?? 10,
            minScore: options.minScore ?? 4,
            excludeIds: options.excludeIds ?? [],
            scanDepth: options.scanDepth ?? 200,
        });
        return result.data as TopMatchResult[];
    },

    /**
     * No-op stub: cache invalidation is handled entirely by the tagsUpdatedAt
     * timestamp check inside computeTagAffinity. When a user's tags change,
     * UserDataContext writes tagsUpdatedAt = serverTimestamp(), and the next
     * computeAffinity call for any pair involving that user will detect the
     * staleness and recompute automatically.
     *
     * Explicit cache deletion (querying tag_affinities by userId and batch-
     * deleting) is not implemented because Firestore does not support OR queries
     * across two different fields in a single pass, requiring two round-trips
     * and a batch delete — more expensive than letting stale docs expire
     * naturally on next access.
     *
     * This stub is exported so callers can call invalidateAffinityCache()
     * without needing to know the implementation strategy, and so explicit
     * deletion can be wired in later without changing any call sites.
     */
    async invalidateAffinityCache(_userId: string): Promise<void> {
        // Intentionally empty — timestamp-based validity handles invalidation.
    },
};
